import React, { useMemo, useState } from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { InputRootProps } from "shared/ui/input/Input"
import { Combobox } from "shared/ui/input/combobox/Combobox"
import { Readonly } from "shared/ui/readonly/Readonly"

import { MAX_LENGTH_PRODUCT_POSITION_PRICE_GROUP } from "entities/product-positions/types/ProductPosition"
import { formatPriceGroupName } from "entities/projects/lib/formatPriceGroupName"

import { useProductPositionFormCoreContext } from "../../contexts/ProductPositionFormCoreContext"
import { useProductPositionFormLoadingContext } from "../../contexts/ProductPositionFormLoadingContext"
import { useProductPositionFormPriceContext } from "../../contexts/ProductPositionFormPriceContext"

export const ProductPositionPriceGroupInput = React.memo(() => {
  const { t } = useTranslation()
  const { isReadonly, isDisabled, formOptions } =
    useProductPositionFormCoreContext()
  const { isLoading, isLoadingPrice, isLoadingPriceGroupOptions } =
    useProductPositionFormLoadingContext()
  const {
    price,
    priceGroupOptions,
    isPriceGroupEditable,
    isPriceGroupOverwritten,
    isSpecialPriceGroup,
    isPriceServiceAvailable
  } = useProductPositionFormPriceContext()
  const { hidePriceGroupInput, hidePrice } = formOptions || {}

  const priceGroups = price?.groups

  const formattedPriceGroups = useMemo(
    () =>
      priceGroups
        ?.map(priceGroup =>
          formatPriceGroupName(priceGroup, t("Price.NoGroup"))
        )
        .join(", ") || "-",
    [priceGroups, t]
  )

  // Follows the toggle live so the field reacts within the same edit session,
  // falling back to the persisted state when the toggle is not rendered.
  const watchedSpecialPriceGroup = useWatch({ name: "isSpecialPriceGroup" }) as
    | boolean
    | undefined
  const hasSpecialPriceGroup = watchedSpecialPriceGroup ?? isSpecialPriceGroup

  const commonProps = useMemo<InputRootProps>(
    () => ({
      "data-testid": "product-position-form__price-groups",
      inputID: "product-position-form__price-groups",
      label: t("Price.PriceGroup"),
      readonly: isReadonly,
      // An active special price group wins over any position-level value, so
      // editing the field would have no effect.
      disabled: isDisabled || (!isReadonly && hasSpecialPriceGroup),
      loading: isLoading || isLoadingPrice || isLoadingPriceGroupOptions
    }),
    [
      isReadonly,
      isDisabled,
      hasSpecialPriceGroup,
      isLoading,
      isLoadingPrice,
      isLoadingPriceGroupOptions,
      t
    ]
  )

  // An active special price group overrides any manually set price group, so the
  // readonly display never shows a manual deviation for those positions.
  const showOverwrittenIcon =
    isPriceGroupOverwritten && !(isReadonly && isSpecialPriceGroup)

  const [inputValue, setInputValue] = useState("")
  const fieldValue = useWatch({ name: "priceGroup" }) as string | null

  const items = useMemo(() => {
    const base = priceGroupOptions ?? []
    const extras: { label: string; value: string }[] = []

    if (fieldValue && !base.some(opt => opt.value === fieldValue)) {
      extras.push({ label: fieldValue, value: fieldValue })
    }

    if (
      inputValue &&
      !base.some(opt => opt.label === inputValue) &&
      inputValue !== fieldValue
    ) {
      extras.push({ label: inputValue, value: inputValue })
    }

    return extras.length ? [...base, ...extras] : base
  }, [priceGroupOptions, fieldValue, inputValue])

  if (
    hidePrice ||
    hidePriceGroupInput ||
    (!isReadonly && !isPriceGroupEditable)
  )
    return null

  if (!isPriceServiceAvailable) {
    return (
      <Readonly.Root data-testid="product-position-form__price-groups">
        <Readonly.Label>{t("Price.PriceGroup")}</Readonly.Label>
        <Readonly.Value>
          <div className="unavailability-message">
            <Icon
              size={IconSize.SMALL}
              name={IconName.CLOSE_CIRCLE}
              className="color-danger"
            />
            {t("Price.ServiceUnavailable")}
          </div>
        </Readonly.Value>
      </Readonly.Root>
    )
  }

  return (
    <Combobox.Root name="priceGroup" {...commonProps} items={items} creatable>
      <Combobox.Label
        icon={showOverwrittenIcon ? IconName.USER_PEN : undefined}
      />
      <Combobox.Control onInputValueChange={setInputValue}>
        <Combobox.Trigger>
          <Combobox.Value placeholder={t("Dropdown.DefaultPlaceholder")} />
          <Combobox.Icon />
        </Combobox.Trigger>
        <Combobox.Portal>
          <Combobox.Positioner sideOffset={8}>
            <Combobox.Popup aria-label={t("Price.PriceGroup")}>
              <Combobox.InputContainer>
                <Combobox.Input
                  maxLength={MAX_LENGTH_PRODUCT_POSITION_PRICE_GROUP}
                />
                <Icon name={IconName.SEARCH} />
              </Combobox.InputContainer>
              <Combobox.Empty />
              <Combobox.List>
                {option => (
                  <Combobox.Item key={option.value} value={option}>
                    <Combobox.ItemIndicator />
                    <Combobox.ItemContent>{option.label}</Combobox.ItemContent>
                  </Combobox.Item>
                )}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Control>
      <Combobox.Readonly items={items}>
        <span className="input__value--readonly">{formattedPriceGroups}</span>
      </Combobox.Readonly>
      <Combobox.ErrorMessage />
    </Combobox.Root>
  )
})
